import { cx } from '@/lib/cx'
import { useT } from '@/lib/i18n/translate'
import {
  PASSWORD_MAX_LEN,
  isCommonPassword,
  passwordLength,
  useCommonPasswords,
} from '@/lib/password'

type RuleState = 'pending' | 'met' | 'failed'

function Rule({ state, children }: { state: RuleState; children: string }) {
  return (
    <li
      className={cx(
        'flex items-center gap-1.5',
        state === 'met' && 'text-success',
        state === 'failed' && 'text-danger',
      )}
    >
      <span aria-hidden className="w-3 text-center">
        {state === 'met' ? '✓' : state === 'failed' ? '✕' : '•'}
      </span>
      {children}
      {/* Announced state for screen readers; the glyph is decoration. */}
      <span className="sr-only">{state}</span>
    </li>
  )
}

export interface PasswordChecklistProps {
  password: string
  /** The account system's minimum. */
  min: number
}

/**
 * Live rules under a new-password field (placed as the `Field` hint): when
 * every line is met, the submitting is guaranteed to pass the backend's checks.
 */
export function PasswordChecklist({ password, min }: PasswordChecklistProps) {
  const t = useT()
  const list = useCommonPasswords()
  const length = passwordLength(password)
  const empty = password === ''
  const tooLong = length > PASSWORD_MAX_LEN
  const common = !empty && list !== undefined && isCommonPassword(password, list)

  const lengthState: RuleState = empty ? 'pending' : length >= min && !tooLong ? 'met' : 'pending'
  const commonState: RuleState = common ? 'failed' : empty || list === undefined ? 'pending' : 'met'

  return (
    <ul className="flex flex-col gap-0.5">
      <Rule state={lengthState}>{t('auth.passwordRuleLength', { min })}</Rule>
      <Rule state={commonState}>{t('auth.passwordRuleCommon')}</Rule>
      {tooLong && <Rule state="failed">{t('auth.passwordRuleMax', { max: PASSWORD_MAX_LEN })}</Rule>}
    </ul>
  )
}
